<template>
  <div class="ep-page">

    <!-- ── Status Banner ──────────────────────────────────── -->
    <div v-if="statusLoaded"
         class="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm"
         :style="bannerStyle">
      <div class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
           style="background: rgba(255,255,255,0.06)">
        <component :is="statusBannerIcon" class="w-[1.125rem] h-[1.125rem]" />
      </div>
      <div class="min-w-0">
        <p class="font-semibold leading-tight">{{ statusBannerTitle }}</p>
        <p v-if="statusBannerDetail" class="text-xs opacity-75 mt-0.5">{{ statusBannerDetail }}</p>
      </div>
    </div>

    <!-- ── Clock + Side Info ──────────────────────────────── -->
    <div class="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">

    <div class="ep-card overflow-hidden xl:col-span-2">
      <!-- Tab Navigation -->
      <div class="flex gap-1 p-1.5 m-4 mb-0 rounded-xl w-fit"
           style="background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06)">
        <button
          :class="['flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150',
                   activeClockTab === 'regular'
                     ? 'bg-indigo-500/20 text-indigo-300 shadow-sm'
                     : 'text-slate-500 hover:text-slate-300']"
          @click="activeClockTab = 'regular'">
          <Clock class="w-4 h-4" /> {{ t('empAttendance.regularClock') }}
        </button>
        <button
          :class="['flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150',
                   activeClockTab === 'overtime'
                     ? 'bg-indigo-500/20 text-indigo-300 shadow-sm'
                     : 'text-slate-500 hover:text-slate-300']"
          @click="activeClockTab = 'overtime'">
          <AlarmClock class="w-4 h-4" /> {{ t('empAttendance.overtimeClock') }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="p-4 md:p-6">
        <div v-show="activeClockTab === 'regular'"><EmployeeClockInterface @stats-updated="handleStatsUpdate" /></div>
        <div v-show="activeClockTab === 'overtime'"><OvertimeClockInOut /></div>
      </div>
    </div>

    <!-- ── Info Cards (side column on desktop) ────────────── -->
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
      <div class="ep-card ep-card-hover p-5">
        <div class="flex items-center gap-3 mb-4">
          <div class="ep-stat-icon ep-stat-indigo"><Clock class="w-5 h-5" /></div>
          <h4 class="text-sm font-semibold text-slate-200">{{ t('empAttendance.workingHours') }}</h4>
        </div>
        <div class="space-y-2.5">
          <div class="flex justify-between text-sm">
            <span class="text-slate-500">{{ t('empAttendance.standardHours') }}</span>
            <span class="font-semibold text-slate-200">{{ t('empAttendance.standardHoursValue') }}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-slate-500">{{ t('empAttendance.weeklyHours') }}</span>
            <span class="font-semibold text-slate-200">{{ t('empAttendance.weeklyHoursValue') }}</span>
          </div>
        </div>
      </div>

      <div class="ep-card ep-card-hover p-5">
        <div class="flex items-center gap-3 mb-4">
          <div class="ep-stat-icon ep-stat-violet"><Shield class="w-5 h-5" /></div>
          <h4 class="text-sm font-semibold text-slate-200">{{ t('empAttendance.quickLinks') }}</h4>
        </div>
        <div class="space-y-2">
          <router-link to="/employee/history"
                       class="flex items-center gap-2.5 text-sm text-slate-400 hover:text-indigo-300 transition-colors">
            <History class="w-4 h-4 text-indigo-400" /> {{ t('empAttendance.viewHistory') }}
          </router-link>
          <router-link to="/employee/leave-requests"
                       class="flex items-center gap-2.5 text-sm text-slate-400 hover:text-indigo-300 transition-colors">
            <Calendar class="w-4 h-4 text-indigo-400" /> {{ t('empAttendance.leaveRequests') }}
          </router-link>
        </div>
      </div>
    </div>

    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
import {
  Clock,
  Calendar,
  Shield,
  History,
  AlarmClock,
  CheckCircle,
  AlertCircle,
  Timer,
  CircleDot,
} from 'lucide-vue-next';
import EmployeeClockInterface from '@/components/attendance/EmployeeClockInterface.vue';
import OvertimeClockInOut from '@/components/attendance/OvertimeClockInOut.vue';
// UI components available globally
import { api } from '@/services/api';

// Active tab for clock interface
const activeClockTab = ref('regular');

// UX-ATT-001: Status banner state
const statusLoaded = ref(false);
const attendanceStatus = ref(null);
const overtimeSession = ref(null);
let statusRefreshInterval = null;

const loadStatus = async () => {
  try {
    const [attRes, otRes] = await Promise.allSettled([
      api.get('/employee/attendance/today'),
      api.get('/employee/overtime-clock/active'),
    ]);

    if (attRes.status === 'fulfilled' && attRes.value.data?.data?.record) {
      attendanceStatus.value = attRes.value.data.data.record;
    } else {
      attendanceStatus.value = null;
    }

    if (otRes.status === 'fulfilled' && otRes.value.data?.data) {
      overtimeSession.value = otRes.value.data.data;
    } else {
      overtimeSession.value = null;
    }

    statusLoaded.value = true;
  } catch (e) {
    console.error('Status banner load error:', e);
  }
};

const bannerPalettes = {
  green:  { color: '#6ee7b7', bg: 'rgba(52,211,153,0.08)',  border: '1px solid rgba(52,211,153,0.25)' },
  amber:  { color: '#fcd34d', bg: 'rgba(251,191,36,0.08)',  border: '1px solid rgba(251,191,36,0.25)' },
  sky:    { color: '#7dd3fc', bg: 'rgba(56,189,248,0.08)',  border: '1px solid rgba(56,189,248,0.25)' },
  neutral:{ color: '#94a3b8', bg: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' },
};

const bannerStyle = computed(() => {
  const map = {
    'status-overtime-active': 'green',
    'status-clocked-in':      'green',
    'status-pending':         'amber',
    'status-under-review':    'amber',
    'status-completed':       'sky',
    'status-idle':            'neutral',
  };
  const p = bannerPalettes[map[statusBannerClass.value] || 'neutral'];
  return { color: p.color, background: p.bg, border: p.border };
});

const statusBannerClass = computed(() => {
  const ot = overtimeSession.value;
  if (ot?.status === 'active') return 'status-overtime-active';
  if (ot?.status === 'pending') return 'status-pending';
  if (ot?.status === 'under_review') return 'status-under-review';

  const att = attendanceStatus.value;
  if (att?.clock_in_time && !att?.clock_out_time && att?.field_approval_status !== 'pending') return 'status-clocked-in';
  if (att?.field_approval_status === 'pending') return 'status-pending';
  if (att?.clock_out_time) return 'status-completed';

  return 'status-idle';
});

const statusBannerIcon = computed(() => {
  const ot = overtimeSession.value;
  if (ot?.status === 'active') return Timer;
  if (ot?.status === 'pending') return AlertCircle;
  if (ot?.status === 'under_review') return CheckCircle;

  const att = attendanceStatus.value;
  if (att?.clock_in_time && !att?.clock_out_time && att?.field_approval_status !== 'pending') return CircleDot;
  if (att?.field_approval_status === 'pending') return AlertCircle;
  if (att?.clock_out_time) return CheckCircle;

  return Clock;
});

const statusBannerTitle = computed(() => {
  const ot = overtimeSession.value;
  if (ot?.status === 'active') return t('empAttendance.overtimeActive');
  if (ot?.status === 'pending') return t('empAttendance.overtimePending');
  if (ot?.status === 'under_review') return t('empAttendance.overtimeUnderReview');

  const att = attendanceStatus.value;
  if (att?.clock_in_time && !att?.clock_out_time && att?.field_approval_status !== 'pending') return t('empAttendance.currentlyWorking');
  if (att?.field_approval_status === 'pending') return t('empAttendance.pendingApproval');
  if (att?.clock_out_time) return t('empAttendance.workCompleted');

  return t('empAttendance.notClockedIn');
});

const statusBannerDetail = computed(() => {
  const ot = overtimeSession.value;
  if (ot?.status === 'active') {
    const remaining = ot.timeRemaining || ot.remaining_seconds || 0;
    if (remaining > 0) {
      const h = Math.floor(remaining / 3600);
      const m = Math.floor((remaining % 3600) / 60);
      return `${h}h ${m}m ${t('empAttendance.remaining')}`;
    }
    return t('empAttendance.timerExpired');
  }

  const att = attendanceStatus.value;
  if (att?.clock_in_time && !att?.clock_out_time && att?.field_approval_status !== 'pending') {
    const rawClockIn = String(att.clock_in_time).replace('Z', '').replace('.000', '');
    const clockIn = new Date(rawClockIn);
    return `Since ${clockIn.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
  }

  return '';
});

const handleStatsUpdate = () => {
  loadStatus();
};

onMounted(() => {
  loadStatus();
  statusRefreshInterval = setInterval(loadStatus, 30000);
});

onUnmounted(() => {
  if (statusRefreshInterval) clearInterval(statusRefreshInterval);
});
</script>

